import {DestroyRef, Injectable, computed, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {HttpClient} from '@angular/common/http';
import {forkJoin, interval, of} from 'rxjs';
import {catchError, retry, switchMap} from 'rxjs/operators';
import {environment} from '../../../../../environments/environment';
import {AnnouncementDto, WhatsNewReadSettings} from '../model/announcement.model';
import {sortByPublishedDesc} from '../model/announcement.util';
import {SettingsService} from '../../../../core/settings/settings.service';
import {AuthenticationService} from '../../../../core/service/auth.service';
import {SETTINGS_KEY_WHATS_NEW_READ} from '../../../../core/settings/settings-keys';
const POLLING_INTERVAL_MS = 60 * 60 * 1000; // 1 hour

@Injectable({providedIn: 'root'})
export class WhatsNewService {
    private readonly apiUrl = environment.apiUrl;
    private readonly http = inject(HttpClient);
    private readonly settingsService = inject(SettingsService);
    private readonly authService = inject(AuthenticationService);
    private readonly destroyRef = inject(DestroyRef);

    /** Vždy seřazené nejnovějším nahoru — viz `sortByPublishedDesc`. */
    readonly announcements = signal<AnnouncementDto[]>([]);
    readonly readIds = signal<string[]>([]);
    readonly unreadCount = computed(() =>
        this.announcements().filter(a => !this.readIds().includes(a.id)).length
    );
    readonly hasUnread = computed(() => this.unreadCount() > 0);

    init(): void {
        forkJoin({
            announcements: this.http.get<AnnouncementDto[]>(`${this.apiUrl}/announcements`).pipe(
                catchError(() => of([] as AnnouncementDto[]))
            ),
            readSettings: this.settingsService.getSettings(SETTINGS_KEY_WHATS_NEW_READ).pipe(
                catchError(() => of(null))
            ),
        }).subscribe(({announcements, readSettings}) => {
            this.announcements.set(sortByPublishedDesc(announcements ?? []));

            const parsed: WhatsNewReadSettings | undefined = Array.isArray(readSettings)
                ? this.settingsService.extractSettings(readSettings, SETTINGS_KEY_WHATS_NEW_READ)
                : undefined;
            const loadedReadIds: string[] = parsed?.readIds ?? [];
            const registeredAt = this.getUserRegistrationDate();

            if (registeredAt) {
                const preRegistrationIds = (announcements ?? [])
                    .filter(a => new Date(a.publishedAt) < registeredAt)
                    .map(a => a.id)
                    .filter(id => !loadedReadIds.includes(id));

                if (preRegistrationIds.length > 0) {
                    const merged = [...loadedReadIds, ...preRegistrationIds];
                    this.readIds.set(merged);
                    this.saveReadIds(merged);
                    return;
                }
            }

            this.readIds.set(loadedReadIds);
        });

        interval(POLLING_INTERVAL_MS).pipe(
            takeUntilDestroyed(this.destroyRef),
            switchMap(() => this.http.get<AnnouncementDto[]>(`${this.apiUrl}/announcements`).pipe(
                catchError(() => of(this.announcements()))
            )),
        ).subscribe(announcements => this.announcements.set(sortByPublishedDesc(announcements ?? [])));
    }

    markAsRead(id: string): void {
        const current = this.readIds();
        if (current.includes(id)) return;
        const updated = [...current, id];
        this.readIds.set(updated);
        this.saveReadIds(updated);
    }

    markAllAsRead(): void {
        const allIds = this.announcements().map(a => a.id);
        this.readIds.set(allIds);
        this.saveReadIds(allIds);
    }

    isRead(id: string): boolean {
        return this.readIds().includes(id);
    }

    private saveReadIds(readIds: string[]): void {
        this.settingsService.setSettings(SETTINGS_KEY_WHATS_NEW_READ, [{[SETTINGS_KEY_WHATS_NEW_READ]: {readIds}}]).pipe(
            retry(2),
            catchError(() => of(null)),
        ).subscribe();
    }

    private getUserRegistrationDate(): Date | null {
        const creationTime = this.authService.currentUser()?.metadata?.creationTime;
        return creationTime ? new Date(creationTime) : null;
    }
}
